import { useCallback, useEffect, useState } from 'react';
import type { FloatingPosition } from './types.ts';

const getStoredPosition = (storageKey: string): FloatingPosition | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored === 'left' || stored === 'right') return stored;
  } catch {
    // ignore
  }
  return null;
};

const setStoredPosition = (storageKey: string, position: FloatingPosition): void => {
  try {
    localStorage.setItem(storageKey, position);
  } catch {
    // ignore
  }
};

export const useFloatingPosition = (
  storageKey: string,
  defaultPosition: FloatingPosition = 'right'
): [FloatingPosition, (position: FloatingPosition) => void] => {
  const [position, setPosition] = useState<FloatingPosition>(() => {
    return getStoredPosition(storageKey) ?? defaultPosition;
  });

  useEffect(() => {
    const stored = getStoredPosition(storageKey);
    if (stored) setPosition(stored);
  }, [storageKey]);

  const updatePosition = useCallback(
    (newPosition: FloatingPosition) => {
      setPosition(newPosition);
      setStoredPosition(storageKey, newPosition);
    },
    [storageKey]
  );

  return [position, updatePosition];
};
